import { Method, AxiosResponse } from "axios";
import { AnimationBuilder } from "@ionic/core";
import { RouteAction, RouteDirection } from "@ionic/vue/dist/types/hooks/router";
import { ToastButton } from "@ionic/core/dist/types/components/toast/toast-interface";

import { ION_MODE, ION_TOAST_POSITION, ION_VARIANT } from "@/types/enum/ionicEnum";
import { ReturnStatus } from "@/types/enum/notification";
import { CatalogItem } from "@/types/supplier";

interface ObjectAny extends NonNullable<unknown> {
    [key: string]: any;
}

interface ObjectUnknown {
    [key: string]: unknown
}

export type FunctionType<T = any, R = any> = (...args: T[]) => R

interface ValidationMessages {
    'multiple': string;
    'min_length': string;
    'max_length': string;
}

interface RequestOption {
    method: Method;
    route: string;
    body?: any;
    query?: ObjectAny;
    responseType?: "arraybuffer" | "blob" | "document" | "json" | "text" | "stream" | undefined;
}

interface UpdateTableOption {
    routePath: string;
    skip?: number;
    take?: number;
    searchKey?: string;
    convertFunction?: (list: any[]) => any[];
    optQueries?: object;
}

interface MsgBoxConfirmOption {
    header?: string;
    contentText: string;
    okTitle?: string;
    cancelTitle?: string;
    okHandler: (value?: any) => any;
    cancelHandler?: () => any;
}

interface OkBoxOption {
    header?: string;
    contentText: string;
    okTitle?: string;
}

interface ToastOption {
    header?: string;
    message?: string;
    cssClass?: string | string[];
    duration?: number;
    buttons?: (ToastButton | string)[];
    position?: ION_TOAST_POSITION;
    translucent?: boolean;
    animated?: boolean;
    icon?: string;
    htmlAttributes?: any;
    color?: ION_VARIANT;
    mode?: ION_MODE;
    keyboardClose?: boolean;
    id?: string;
    enterAnimation?: AnimationBuilder;
    leaveAnimation?: AnimationBuilder;
}

interface ScrollPositionCoordinates {
    behavior?: 'auto' | 'smooth';
    left: number;
    top: number;
}

interface ScrollElement extends ScrollPositionCoordinates {
    el: Element | string;
}

declare type ScrollPosition = ScrollPositionCoordinates | ScrollElement

interface ErrorHandlerParams<T = undefined> {
    error: T,
    handler?: ((error: T) => any | Promise<any>) | string;
    offset?: number
}

interface OpenProductParams {
    amount?: number;
    product: CatalogItem;
    supplierId: string;
    listId?: string;
    offset?: number;
    catalog?: boolean;
    listItemId?: string;
    buttons?: boolean;
    targetStock?: number;
    privateSupplier?: boolean;
}

type NavigateFunction = (location: any, routerDirection?: RouteDirection, routerAction?: RouteAction, routerAnimation?: AnimationBuilder) => void

interface RegisterData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    passwordRepeat: string;
    agb: false
}

interface AuthCredentials {
    grant_type?: string;
    username?: string;
    password?: string;
    refresh_token?: string;
}

interface AuthResult {
    valid: boolean;
    isAdmin?: boolean;
}

interface PaginationResponse {
    data: any[];
    count: number;
}

class AJVErrorBody {
    constructor(
        public keyword: string,
        public instancePath: string,
        public schemaPath: string,
        public params: Record<string, string>,
        public propertyName?: string,
        public message?: string,
        public schema?: any,
        public parentSchema?: Record<string, unknown>,
        public data?: any
    ) {
    }
}

const isAJVErrorBody = (object: unknown): object is AJVErrorBody => {
    return isInstanceOf<AJVErrorBody>(object, [
        'keyword',
        'instancePath',
        'schemaPath',
        'params'
    ])
}

class AJVErrorObject {
    constructor(
        public body: AJVErrorBody[]
    ) {
    }
}

const isAJVErrorObject = (object: unknown): object is AJVErrorObject => {
    if (!isInstanceOf<AJVErrorObject>(object, [
        'body',
    ])) return false

    return isAJVErrorBody(object.body[0])
}

class ErrorReturnType {
    constructor(public status: ReturnStatus, public message: string) {
    }
}

const isErrorReturnType = (object: unknown): object is ErrorReturnType => {
    return isInstanceOf<ErrorReturnType>(object, [
        'status',
        'message'
    ])
}


const isInstanceOf = <T extends object>(object: unknown, keys: (keyof T)[]): object is T => {
    if (typeof object !== 'object' || object === null) return false

    for (const key of keys) {
        if (!(key in object)) return false
    }

    return true
}

type CustomizedErrorResponse = AJVErrorBody | ErrorReturnType | undefined
type CustomizedResponse = AxiosResponse | CustomizedErrorResponse

export {
    ObjectAny,
    ObjectUnknown,
    ValidationMessages,
    RequestOption,
    UpdateTableOption,
    MsgBoxConfirmOption,
    OkBoxOption,
    ToastOption,
    ScrollPositionCoordinates,
    ScrollPosition,
    ErrorHandlerParams,
    OpenProductParams,
    NavigateFunction,
    RegisterData,
    AuthCredentials,
    AuthResult,
    PaginationResponse,
    AJVErrorObject,
    AJVErrorBody,
    CustomizedResponse,
    CustomizedErrorResponse,
    ErrorReturnType,
    isInstanceOf,
    isErrorReturnType,
    isAJVErrorObject,
    isAJVErrorBody,
}
